import merge from 'lodash-es/merge'

import { ResponseItem } from '@rewardops/utils'

import { getAttr } from './base-utils'

const supplierItemsPath = ['supplier_items', 0]

export interface ItemType {
  id: string
  total: string
  brand: string
  name: string
  imageUrl: string
  itemTotal: number
}

const itemTargetAttrs = [
  {
    key: 'id',
    path: 'id',
  },
  {
    key: 'name',
    path: 'name',
  },
  {
    key: 'imageUrl',
    path: ['images', 0, 'url'],
  },
  {
    key: 'regularPrice',
    path: (programCurrency: string) => [
      ...supplierItemsPath,
      'prices',
      'regular_price',
      programCurrency,
    ],
    defaultValue: 0,
  },
  {
    key: 'salePrice',
    path: (programCurrency: string) => [
      ...supplierItemsPath,
      'prices',
      'sale_price',
      programCurrency,
    ],
    defaultValue: 0,
  },
  {
    key: 'orderToken',
    path: [...supplierItemsPath, 'order_token'],
  },
]

export const extractItemDetails = (
  item: object,
  programCurrency: string,
): any => {
  const getAttrFromObj = getAttr(item, programCurrency)
  const attrs = (itemTargetAttrs as any).map(getAttrFromObj)
  return merge({}, ...attrs)
}

export const hasCustomProperty = (
  item: ResponseItem,
  property: string,
): boolean => {
  const customProperty = getCustomProperty(item, property)

  if (!customProperty) {
    return false
  }

  return customProperty?.value === 'true'
}

export const getCustomProperty = (item: ResponseItem, property: string) => {
  const customProperties =
    item?.supplier_items?.[0]?.custom_properties ??
    (item as any)?.customProperties

  return customProperties?.find((prop: any) => prop?.key === property)
}

export const getProgramDeliveryTermsFromMultipleItems = (
  items: ResponseItem[],
  language = 'en',
): string => {
  const item = items?.[0]
  const terms = [
    item?.supplier_items?.[0]?.fulfillment?.instructions
      ?.replaceAll('<p>', '')
      ?.replaceAll('</p>', '') ??
      (item as any)?.termsAndConditions
        ?.replaceAll('<p>', '')
        ?.replaceAll('</p>', '') ??
      '',
  ]

  items?.forEach((item) => {
    const property = getCustomProperty(
      item,
      `program_delivery_terms_${language}`,
    )?.value

    if (property?.length) {
      terms?.push(property)
    }
  })

  if (terms?.join('')?.length) {
    return `${terms?.join('\n\n')}\n\n`
  }

  return ''
}

export const getEarnRate = (item: ResponseItem, programCurrency: string) => {
  const supplierItem = item?.supplier_items?.[0]

  const hasVariableEarn =
    typeof supplierItem?.max_earn_rate == 'number' &&
    supplierItem?.max_earn_rate > 0

  const maxEarnRate = Number(supplierItem?.max_earn_rate ?? 0)

  const earnRate =
    maxEarnRate > 0
      ? maxEarnRate
      : Number(supplierItem?.earn?.earn_rate_cash?.rate)

  const flatEarnRate = Number(supplierItem?.earn?.earn_fixed?.[programCurrency])

  const isAffiliateNonRedeemable = hasCustomProperty(
    item,
    'non_affiliate_brand',
  )

  return {
    hasVariableEarn,
    maxEarnRate,
    earnRate,
    flatEarnRate,
    isAffiliateNonRedeemable,
  }
}

export const getAnalyticsItems = (items: any) =>
  items?.map((item: any) => ({
    id: item?.id,
    name: item?.name,
    price: item?.price,
    quantity: item?.quantity,
  }))
